// Send an invoice to the customer.
//
// Exists because "sent" was being written to the database by a path that never
// emailed anything. Invoices.sendInvoice() flipped the status, updated the row
// and reported success - so the dashboard counted the invoice as sent, and the
// customer never heard from anyone.
//
// The rule this encodes: an invoice is only marked sent once the send succeeded.

import { generateInvoicePdf } from './invoicePdf.js';

/**
 * Generate the PDF and hand it to the sendInvoiceEmail callable.
 * Throws on failure - callers must not record "sent" unless this resolves.
 */
export async function sendInvoiceEmail({ job, invoice, uid, vendorName }) {
  const { doc, model } = await generateInvoicePdf({ job, invoice, uid });

  const dataUri = doc.output('datauristring');
  const pdfBase64 = String(dataUri).split(',')[1];
  if (!pdfBase64) throw new Error('Could not encode the invoice PDF.');

  const payload = {
    customerEmail: model.customer.email,
    customerName: model.customer.name,
    invoiceNumber: model.invoiceNumber || `INV-${Date.now()}`,
    pdfBase64,
    userId: uid,
    vendor: vendorName || model.vendor.name,
  };

  const missing = ['customerEmail', 'customerName', 'invoiceNumber', 'pdfBase64']
    .filter((f) => !payload[f]);
  if (missing.length) {
    throw new Error(`Cannot send: missing ${missing.join(', ')}.`);
  }

  const call = firebase.functions().httpsCallable('sendInvoiceEmail');
  const result = await call(payload);

  // The callable resolves with { success } - treat anything else as a failure
  // rather than assuming it worked.
  if (!result || !result.data || !result.data.success) {
    throw new Error((result && result.data && result.data.error) || 'The email service rejected the invoice.');
  }

  return { ...result.data, model };
}
